// File: frontend/src/components/ui/DetailDrawer.js
//
// THE DETAIL PANEL. One record, opened from a list, over the list.
//
// WHY IT EXISTS
//   Eleven pages opened a record in their own <Drawer>, each with its own idea
//   of width, sheet radius, focus and naming. Most were a fixed 400–520px from
//   600px upward, which is fine on a wide monitor and ruinous on a laptop: at
//   1024px a 520px panel beside the 208px sidebar leaves 296px of list — too
//   narrow to read, behind a backdrop that has already made it inert. And only
//   TransactionDrawer announced itself to a screen reader at all.
//
// THE RULE
//   A side panel only when there is WORKSPACE left beside it: the sidebar, the
//   panel, and at least MIN_WORKSPACE of list. Below that the record takes the
//   whole screen, because half-covering a list only makes both unreadable. The
//   threshold therefore depends on the panel's width rather than on "phone or
//   not" — a 440px panel goes side-by-side at 1120px, a 520px one at 1200px.
//
// WHAT EVERY PANEL GETS, SO NO PAGE HAS TO REMEMBER
//   · a labelled dialog: role, aria-modal and a name (`labelledBy` points at the
//     panel's own visible title; `ariaLabel` when it has none);
//   · initial focus on the panel itself, so that name is read on open;
//   · Escape, wired explicitly, not left to the page;
//   · focus restored to the row that opened it (the panel unmounts on close);
//   · browser Back closes the panel instead of leaving the page underneath —
//     on a phone Back is the primary gesture, and a full-screen panel that Back
//     walks straight past is a trap;
//   · reduced motion honoured.
//
// WHAT IT DELIBERATELY DOES NOT DO
//   Draw a header. Every caller already has one — an amount, a name, a status —
//   and its own close button; a shared header would either flatten them or grow
//   a prop for every variation. This owns the SURFACE and its behaviour; the
//   caller owns what is on it.

import React, { useCallback, useEffect, useRef } from "react";
import { Drawer, useMediaQuery } from "@mui/material";
import { useReducedMotion, duration } from "./motion";
import { MIN_TAP } from "./tokens";

// AdminLayout's sidebar. Kept here as a number rather than imported because the
// sidebar does not export it, and this is a layout threshold, not a style.
const SIDEBAR_WIDTH = 208;

// The least list worth keeping visible beside a panel. Below this the list is a
// strip nobody can read, and the panel should simply take the screen.
export const MIN_WORKSPACE = 472;

// A step-back scheduled by a panel that just closed (see the history effect).
// Module-level because the panel that closes and the one that opens next are
// different component instances.
let pendingStepBack = null;

/** Minimum viewport width at which a panel of `width` goes side-by-side. */
export function sidePanelMinWidth(width) {
  return SIDEBAR_WIDTH + width + MIN_WORKSPACE;
}

/**
 * @param {boolean}  open
 * @param {Function} onClose      (event, reason) — reason "backButton" on Back
 * @param {number}   [width]      side-panel width in px (440)
 * @param {string}   [labelledBy] id of the panel's visible title
 * @param {string}   [ariaLabel]  accessible name when there is no visible title
 * @param {boolean}  [disableClose] refuse to close (e.g. while saving)
 * @param {boolean}  [closeOnBack]  default true
 * @param {object}   [PaperProps]   merged over the defaults; `sx` merged too
 */
export default function DetailDrawer({
  open = false,
  onClose,
  width = 440,
  labelledBy,
  ariaLabel,
  disableClose = false,
  closeOnBack = true,
  children,
  PaperProps = {},
  sx,
}) {
  const reduced = useReducedMotion();
  const side = useMediaQuery(`(min-width:${sidePanelMinWidth(width)}px)`, { noSsr: true });

  const disableRef = useRef(disableClose);
  disableRef.current = disableClose;

  const requestClose = useCallback((event, reason) => {
    if (disableRef.current) return;
    onClose?.(event, reason);
  }, [onClose]);

  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;

  // Escape, explicitly. MUI's Modal emits it through onClose, but a keydown on
  // the paper also covers focus sitting inside a nested popper.
  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      requestClose(e, "escapeKeyDown");
    }
    PaperProps.onKeyDown?.(e);
  }, [requestClose, PaperProps]);

  // ── Browser Back closes the panel ─────────────────────────────────────────
  // Opening pushes one history entry at the SAME address (a copy of the
  // router's own state, marked as ours). Back pops it, which is heard here and
  // closes the panel; the page underneath never moved. Closing any other way
  // steps back over that entry — but only if it is still the current one: if
  // something inside the panel navigated elsewhere, stepping back would undo
  // the reader's navigation, so the entry is left as a harmless same-address
  // step instead.
  useEffect(() => {
    if (!open || !closeOnBack || typeof window === "undefined" || !window.history?.pushState) {
      return undefined;
    }
    const token = `dd-${Math.random().toString(36).slice(2)}`;
    let ours = true;
    try {
      if (pendingStepBack && window.history.state?.__detailDrawer) {
        // A panel closed a moment ago and its entry is still on top — React
        // StrictMode's effect re-run, or close-then-open in one tick. Take that
        // entry over rather than stacking a second one on top of it; otherwise
        // the scheduled step-back would land on it later and look like a Back.
        clearTimeout(pendingStepBack);
        pendingStepBack = null;
        window.history.replaceState({ ...window.history.state, __detailDrawer: token }, "");
      } else {
        window.history.pushState({ ...(window.history.state || {}), __detailDrawer: token }, "");
      }
    } catch {
      return undefined;
    }

    const onPop = () => {
      // Still (or again, after Forward) on OUR entry: nothing left the panel.
      // Anywhere else — the page's own entry, or a panel entry that is no
      // longer live — means the reader stepped back past this panel.
      if (window.history.state?.__detailDrawer === token) return;
      ours = false;
      if (disableRef.current) {
        // Refused (a save is in flight): put the entry back so the next Back
        // still lands here rather than silently leaving the page.
        try { window.history.pushState({ ...(window.history.state || {}), __detailDrawer: token }, ""); ours = true; } catch { /* nothing to restore */ }
        return;
      }
      closeRef.current?.(null, "backButton");
    };

    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (ours && window.history.state?.__detailDrawer === token) {
        // Stepped over on the next tick, not now, so a panel opening in the same
        // tick can adopt this entry instead (see above).
        pendingStepBack = setTimeout(() => {
          pendingStepBack = null;
          if (window.history.state?.__detailDrawer === token) window.history.back();
        }, 0);
      }
    };
  }, [open, closeOnBack]);

  const surfaceSx = side
    ? { width, maxWidth: "100vw", height: "100%", borderLeft: "1px solid", borderColor: "divider" }
    : { width: "100%", maxWidth: "100vw", height: "100%" };

  // THE TOUCH FLOOR, FOR EVERY PANEL AT ONCE (§42). On a phone the header's
  // close button and compact actions measured 27–31px across the eleven panels
  // (the sweep at 320–430px). Below `sm` every button in a panel meets the
  // product's 44px minimum; from `sm` up desktop density is left exactly as
  // each panel drew it.
  const touchFloorSx = {
    "& .MuiButton-root, & .MuiIconButton-root": {
      minHeight: { xs: MIN_TAP, sm: "unset" },
    },
    "& .MuiIconButton-root": {
      minWidth: { xs: MIN_TAP, sm: "unset" },
    },
  };

  const { sx: paperSx, onKeyDown: _ignored, ...paperRest } = PaperProps;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={requestClose}
      transitionDuration={duration(reduced, "base")}
      // Torn down on close, so MUI returns focus to the row that opened it
      // rather than to a detached node.
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": labelledBy || undefined,
        "aria-label": labelledBy ? undefined : ariaLabel,
        // Takes initial focus itself, so the name above is what gets read.
        tabIndex: -1,
        ...paperRest,
        onKeyDown,
        "data-detail-drawer": side ? "side" : "full",
        sx: {
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          "&:focus-visible": { outline: "none" },
          ...touchFloorSx,
          ...surfaceSx,
          ...paperSx,
          ...sx,
        },
      }}
    >
      {children}
    </Drawer>
  );
}
